import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { memo, useState } from 'react';
import useProperty from '../../property/hooks/useProperty';

const createBookingMutation = gql`
    mutation createBooking(
        $propertyId: Int!
        $reference: String!
        $guestName: String!
        $contactPhone: String
        $earnings: String
        $checkinDate: String!
        $checkoutDate: String!
    ) {
        createBooking(
            propertyId: $propertyId
            reference: $reference
            guestName: $guestName
            contactPhone: $contactPhone
            earnings: $earnings
            checkinDate: $checkinDate
            checkoutDate: $checkoutDate
        )
    }
`;

interface BookingCreateFormProps {
    open: boolean;
    onClose: () => void;
}

const BookingCreateForm = memo(({ open, onClose }: BookingCreateFormProps) => {
    const { propertyId } = useProperty();
    const [createBooking, { loading, error }] = useMutation(
        createBookingMutation
    );
    const [reference, setReference] = useState('');
    const [guestName, setGuestName] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [earnings, setEarnings] = useState('');
    const [checkinDate, setCheckinDate] = useState('');
    const [checkoutDate, setCheckoutDate] = useState('');
    const [validationErrors, setValidationErrors] = useState<
        Record<string, string>
    >({});
    const validateForm = () => {
        const errors: Record<string, string> = {};

        if (!reference.trim()) errors.reference = 'Reference is required';
        if (!guestName.trim()) errors.guestName = 'Guest name is required';
        if (!checkinDate.trim())
            errors.checkinDate = 'Check-in date is required';
        if (!checkoutDate.trim())
            errors.checkoutDate = 'Check-out date is required';
        if (
            checkinDate &&
            checkoutDate &&
            new Date(checkinDate) >= new Date(checkoutDate)
        ) {
            errors.checkoutDate = 'Check-out date must be after check-in date';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            await createBooking({
                variables: {
                    propertyId,
                    reference,
                    guestName,
                    contactPhone: contactPhone || null,
                    earnings: earnings || null,
                    checkinDate,
                    checkoutDate,
                },
            });

            // Reset form
            setReference('');
            setGuestName('');
            setContactPhone('');
            setEarnings('');
            setCheckinDate('');
            setCheckoutDate('');
            setValidationErrors({});
            onClose();
        } catch {
            // Error is handled by Apollo Client
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'calc(100% - 32px)',
                    maxWidth: 500,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: { xs: 2, sm: 4 },
                    maxHeight: '90vh',
                    overflow: 'auto',
                }}
            >
                <Typography variant="h6" component="h2" gutterBottom>
                    Create New Booking
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error.message}
                    </Alert>
                )}

                <Box
                    component="form"
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                    <TextField
                        label="Reference"
                        value={reference}
                        onChange={e => setReference(e.target.value)}
                        error={!!validationErrors.reference}
                        helperText={validationErrors.reference}
                        required
                        fullWidth
                    />

                    <TextField
                        label="Guest Name"
                        value={guestName}
                        onChange={e => setGuestName(e.target.value)}
                        error={!!validationErrors.guestName}
                        helperText={validationErrors.guestName}
                        required
                        fullWidth
                    />

                    <TextField
                        label="Contact Phone"
                        value={contactPhone}
                        onChange={e => setContactPhone(e.target.value)}
                        error={!!validationErrors.contactPhone}
                        helperText={validationErrors.contactPhone}
                        fullWidth
                    />

                    <TextField
                        label="Earnings"
                        value={earnings}
                        onChange={e => setEarnings(e.target.value)}
                        error={!!validationErrors.earnings}
                        helperText={validationErrors.earnings}
                        fullWidth
                    />

                    <TextField
                        label="Check-in Date"
                        type="date"
                        value={checkinDate}
                        onChange={e => setCheckinDate(e.target.value)}
                        error={!!validationErrors.checkinDate}
                        helperText={validationErrors.checkinDate}
                        required
                        fullWidth
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />

                    <TextField
                        label="Check-out Date"
                        type="date"
                        value={checkoutDate}
                        onChange={e => setCheckoutDate(e.target.value)}
                        error={!!validationErrors.checkoutDate}
                        helperText={validationErrors.checkoutDate}
                        required
                        fullWidth
                        slotProps={{
                            htmlInput: {
                                min: checkinDate || undefined,
                            },
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                    >
                        <Button onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            startIcon={
                                loading ? <CircularProgress size={20} /> : null
                            }
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
});

export default BookingCreateForm;
